import { SharedVideo } from "@authory-packages/core";
import { throttle } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useWindowSize } from "../../hooks/useWindowSize";
import { theme } from "../../theme/theme";
import { formatTime } from "../../utils/formatMediaTime";
import { V3IconDownload, V3IconFullscreen, V3IconMuted, V3IconPause, V3IconPlay, V3IconVolFull } from "../Icons";
import { V3Loader } from "../Loader";
import { V3RangeInput } from "../RangeInput";
import { V3Tooltip } from "../Tooltip";
import { V3HeadlineH5 } from "../Typography";
import { V3Vid, V3VidControls, V3VideoLoaderPos, V3VidWrapper, VolumeRange, VolumeRockerWrapper } from "./V3VideoPlayer.styles";

const isVideoFullScreen = function () {
    return !!(
        document.fullscreen ||
        //@ts-ignore
        document.webkitIsFullScreen ||
        //@ts-ignore
        document.mozFullScreen ||
        //@ts-ignore
        document.msFullscreenElement ||
        document.fullscreenElement
    );
};

const IDLE_TROTTLE_INTERVAL = 100;
const IDLE_HIDE_CONTROL_INTERVAL = 3000;

type V3VideoPlayerProps = {
    attachment: SharedVideo;
};

export const V3VideoPlayer = ({ attachment }: V3VideoPlayerProps) => {
    const size = useWindowSize();

    const [hasError, setHasError] = useState(false);
    const [idleTimer, setIdleTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [controlsVisible, setControlsVisible] = useState(true);

    const [progressValue, setProgressValue] = useState("0");
    const [totalDuration, setTotalDuration] = useState("00:00");
    const [currentTime, setCurrentTime] = useState("00:00");

    const [supportsVideo, setSupportsVideo] = useState(false);
    const [supportsFullscreen, setSupportsFullscreen] = useState(false);

    const [isMuted, setIsMuted] = useState(false);
    const [isPaused, setIsPaused] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);

    const [bufferTimer, setBufferTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [buffering, setBuffering] = useState(false);

    const mainWrapperEl = useRef<HTMLDivElement | null>(null);
    const videoEl = useRef<HTMLVideoElement | null>(null);
    const controlsEl = useRef<HTMLUListElement | null>(null);

    const hideCustomControlPannel = (size.width && size.width < theme.breakpoints.tablet) || false;

    const maxHeight = mainWrapperEl.current?.offsetHeight || undefined;

    useEffect(() => {
        const supportsVideo = !!document.createElement("video").canPlayType;
        setSupportsVideo(supportsVideo);

        setSupportsFullscreen(supportsFullscreen);

        const currVideoEl = videoEl.current;
        // Hide the default controls
        if (currVideoEl) currVideoEl.controls = false;

        const FSChange = (isFS: boolean) => {
            const currVideoEl = videoEl.current;
            if (!currVideoEl) return;

            if (!isFS && hideCustomControlPannel) {
                currVideoEl.playsInline = true;
                currVideoEl.controls = false;
                setSupportsVideo(true);
            }

            setIsFullscreen(isFS);
        };

        const fsChange = () => FSChange(!!(document.fullscreen || document.fullscreenElement));
        //@ts-ignore
        const webkitFsChange = () => FSChange(!!document.webkitIsFullScreen);
        //@ts-ignore
        const mozFsChange = () => FSChange(!!document.mozFullScreen);
        //@ts-ignore
        const msFsChange = () => FSChange(!!document.msFullscreenElement);
        //@ts-ignore
        const webkitEndVideoFsChange = () => {
            setIsPaused(true);
            FSChange(false);
        };

        if (document) {
            document.addEventListener("fullscreenchange", fsChange);
            document.addEventListener("webkitfullscreenchange", webkitFsChange);
            document.addEventListener("mozfullscreenchange", mozFsChange);
            document.addEventListener("msfullscreenchange", msFsChange);
            if (currVideoEl) currVideoEl.addEventListener("webkitendfullscreen", webkitEndVideoFsChange);
        }

        // cleanup
        return () => {
            document.removeEventListener("fullscreenchange", fsChange);
            document.removeEventListener("webkitfullscreenchange", webkitFsChange);
            document.removeEventListener("mozfullscreenchange", mozFsChange);
            document.removeEventListener("msfullscreenchange", msFsChange);
            if (currVideoEl) currVideoEl.removeEventListener("webkitendfullscreen", webkitEndVideoFsChange);
        };
    }, [videoEl.current]);

    useEffect(() => {}, [videoEl.current?.muted, videoEl.current?.paused, videoEl.current?.ended]);

    const TogglePlay = () => {
        const currVideoEl = videoEl.current;
        if (!currVideoEl || (isFullscreen && hideCustomControlPannel)) return;
        if (currVideoEl.paused || currVideoEl.ended) currVideoEl.play();
        else currVideoEl.pause();
        setIsPaused(!isPaused);
    };

    const ToggleMuted = () => {
        const currVideoEl = videoEl.current;
        if (!currVideoEl) return;
        currVideoEl.muted = !currVideoEl.muted;
        setIsMuted(!isMuted);
    };

    const ToggleFullscreen = () => {
        const currVideoEl = videoEl.current;
        if (!currVideoEl) return;

        if (isVideoFullScreen()) {
            currVideoEl.controls = false;
            currVideoEl.playsInline = true;
            setSupportsVideo(true);

            if (document.exitFullscreen) document.exitFullscreen();
            //@ts-ignore
            else if (document.mozCancelFullScreen) document.mozCancelFullScreen();
            //@ts-ignore
            else if (document.webkitCancelFullScreen) document.webkitCancelFullScreen();
            //@ts-ignore
            else if (document.msExitFullscreen) document.msExitFullscreen();
            //@ts-ignore
            else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
            setIsFullscreen(false);
        } else {
            const currWrapperEl = mainWrapperEl.current;
            if (!currWrapperEl) return;

            currVideoEl.playsInline = false;

            let targetElem: HTMLVideoElement | HTMLDivElement | undefined;

            if (hideCustomControlPannel) {
                currVideoEl.controls = true;
                setSupportsVideo(false);
                targetElem = currVideoEl;
            } else {
                currVideoEl.controls = false;
                setSupportsVideo(true);
                targetElem = currWrapperEl;
            }

            //@ts-ignore
            if (targetElem.requestFullscreen) targetElem.requestFullscreen();
            //@ts-ignore
            else if (targetElem.mozRequestFullScreen) targetElem.mozRequestFullScreen();
            //@ts-ignore
            else if (targetElem.webkitRequestFullScreen) targetElem.webkitRequestFullScreen();
            //@ts-ignore
            else if (targetElem.msRequestFullscreen) targetElem.msRequestFullscreen();
            //@ts-ignore
            else if (targetElem.webkitEnterFullscreen) targetElem.webkitEnterFullscreen();

            setIsFullscreen(true);
        }
    };

    const updateCurrentTime = (time: number) => {
        //update video current time
        const currentTime = Math.round(time);
        const formattedTime = formatTime(currentTime);
        setCurrentTime(`${formattedTime.minutes}:${formattedTime.seconds}`);
    };

    const updateTotalDuration = (time: number) => {
        //update video current time
        const currentTime = Math.round(time);
        const formattedTime = formatTime(currentTime);
        setTotalDuration(`${formattedTime.minutes}:${formattedTime.seconds}`);
    };

    // hideControls hides the video controls when not in use

    const hideControls = () => {
        const currVideoEl = videoEl.current;
        if (!currVideoEl) return;

        // if the video is paused, the controls must remain visible
        if (currVideoEl.paused) return;

        setControlsVisible(false);
    };

    // showControls displays the video controls
    const showControls = () => {
        setControlsVisible(true);
    };

    const throttledMouseMove = throttle(() => {
        setControlsVisible(true);
        if (idleTimer) clearTimeout(idleTimer);

        const fn = setTimeout(function () {
            hideControls();
        }, IDLE_HIDE_CONTROL_INTERVAL);

        setIdleTimer(fn);
    }, IDLE_TROTTLE_INTERVAL);

    const showMutedVolume = isMuted || (videoEl && videoEl.current?.volume === 0);

    return (
        <V3VidWrapper ref={mainWrapperEl} isFullScreen={isFullscreen} onMouseMove={throttledMouseMove} hideMouse={!controlsVisible}>
            {buffering && (
                <V3VideoLoaderPos>
                    <V3Loader loadingText="" background="transparent" />
                </V3VideoLoaderPos>
            )}
            <V3Vid
                ref={videoEl}
                controls
                isFullScreen={isFullscreen}
                preload="metadata"
                playsInline={!isFullscreen}
                poster={attachment.thumbnailUrl || undefined}
                onClick={TogglePlay}
                maxHeight={maxHeight}
                onEnded={() => {
                    setIsPaused(true);
                }}
                onError={() => {
                    setHasError(true);
                }}
                onWaiting={() => {
                    if (bufferTimer) clearTimeout(bufferTimer);
                    const fn = setTimeout(function () {
                        setBuffering(true);
                    }, 800);
                    setBufferTimer(fn);
                }}
                onPlaying={() => {
                    if (bufferTimer) clearTimeout(bufferTimer);
                    setBuffering(false);
                }}
                onLoadedMetadata={() => {
                    const currVideoEl = videoEl.current;
                    if (!currVideoEl) return;

                    const currControlsEl = controlsEl.current;
                    if (!currControlsEl) return;

                    updateTotalDuration(currVideoEl.duration);
                }}
                onTimeUpdate={() => {
                    const currVideoEl = videoEl.current;
                    if (!currVideoEl) return;

                    setProgressValue(((currVideoEl.currentTime * 100) / currVideoEl.duration).toString());

                    updateTotalDuration(currVideoEl.duration);
                    updateCurrentTime(currVideoEl.currentTime);
                }}
                onMouseEnter={showControls}
                onMouseLeave={hideControls}
            >
                <source src={attachment.videoUrl} type={attachment.contentType || "video/mp4"}></source>
            </V3Vid>
            {!hasError && (
                <V3VidControls onMouseEnter={showControls} show={supportsVideo && controlsVisible} ref={controlsEl}>
                    <li>{isPaused ? <V3IconPlay onClick={TogglePlay} /> : <V3IconPause onClick={TogglePlay} />}</li>
                    <li>
                        <V3HeadlineH5>
                            <strong>{currentTime}</strong>
                        </V3HeadlineH5>
                    </li>
                    <li>
                        <V3RangeInput
                            min="0"
                            max="100"
                            value={progressValue}
                            step="0.01"
                            onChange={(e) => {
                                const currVideoEl = videoEl.current;
                                if (!currVideoEl) return;

                                setProgressValue(e.target.value);

                                const targetVal = parseFloat(e.target.value);

                                if (isFinite(targetVal)) {
                                    currVideoEl.currentTime = (targetVal / 100) * currVideoEl.duration;
                                }
                            }}
                        />
                    </li>
                    <li>
                        <V3HeadlineH5>
                            <strong>{totalDuration}</strong>
                        </V3HeadlineH5>
                    </li>
                    <li>
                        <V3Tooltip
                            referenceContent={
                                <V3Tooltip
                                    background="transparent"
                                    referenceContent={
                                        <>{showMutedVolume ? <V3IconMuted onClick={ToggleMuted} /> : <V3IconVolFull onClick={ToggleMuted} />}</>
                                    }
                                    tooltipContent={
                                        <VolumeRockerWrapper>
                                            <VolumeRange>
                                                <V3RangeInput
                                                    min="0"
                                                    max="1"
                                                    defaultValue="0.5"
                                                    step="0.05"
                                                    barColor={theme.v3.colors.grey70}
                                                    progressColor={"white"}
                                                    thumbSize={10}
                                                    onChange={(e) => {
                                                        const currVideoEl = videoEl.current;
                                                        if (!currVideoEl) return;

                                                        if (currVideoEl.muted) {
                                                            currVideoEl.muted = false;
                                                            setIsMuted(false);
                                                        }

                                                        const targetVal = parseFloat(e.target.value);

                                                        if (isFinite(targetVal)) {
                                                            currVideoEl.volume = targetVal;
                                                        }
                                                    }}
                                                />
                                            </VolumeRange>
                                        </VolumeRockerWrapper>
                                    }
                                />
                            }
                            tooltipContent={showMutedVolume ? "Unmute" : "Mute"}
                            placement="bottom"
                        />
                    </li>
                    <li>
                        <V3IconDownload
                            onClick={() => {
                                window.open(attachment.videoUrl);
                            }}
                        />
                    </li>
                    <li>{!isFullscreen ? <V3IconFullscreen onClick={ToggleFullscreen} /> : <V3IconFullscreen onClick={ToggleFullscreen} />}</li>
                </V3VidControls>
            )}
        </V3VidWrapper>
    );
};
